import toastTrigger from '@ui/toast/toast-trigger';
import {
  GetUploadEndpoint,
  getUploadFilename,
  SetItemContentType,
  UploadFileToProvider,
} from '@ui/uploads/validations';
import { useCallback, useEffect, useRef, useState } from 'react';
import { isObject } from 'underscore';

export const useImageUpload = (setImg?: (str: string) => void): [loading: boolean, upload: () => void] => {
  const [loading, setLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const handleFile = useCallback(
    async (e: Event) => {
      try {
        if (!(e.target instanceof HTMLInputElement)) return;

        if (!e.target.files?.[0]) return;
        setLoading(true);
        const file = e.target.files[0];
        const endpoint = await GetUploadEndpoint({
          filename: getUploadFilename(file.name),
        });
        if (isObject(endpoint) && endpoint.url) {
          const finalUrl = await UploadFileToProvider(
            {
              url: endpoint.url,
              fields: endpoint.fields,
              cdnHost: endpoint?.cdnHost,
            },
            file
          );

          SetItemContentType({ filename: finalUrl, contentType: file.type }).catch(console.error);

          if (finalUrl) {
            setImg?.(finalUrl);
          }
        }
      } catch (e) {
        console.error(e);
        toastTrigger('Image Upload Error', {
          description: 'We ran into an issue uploading that image.',
          type: 'error',
        });
      } finally {
        setLoading(false);
        if (inputRef.current) {
          inputRef.current.removeEventListener('change', handleFile);
          document.body.removeChild(inputRef.current);
        }
      }
    },
    [setImg]
  );

  useEffect(() => {
    return () => {
      if (inputRef.current) {
        try {
          document.body.removeChild(inputRef.current);
        } catch {
          // do nothing
        }
      }
    };
  }, []);

  const upload = useCallback(() => {
    inputRef.current = document.createElement('input');
    inputRef.current.style.position = 'fixed';
    inputRef.current.style.top = '-100vh';
    inputRef.current.style.left = '-100vw';
    inputRef.current.setAttribute('type', 'file');
    inputRef.current.addEventListener('change', handleFile);
    document.body.appendChild(inputRef.current);
    inputRef.current.click();
  }, [handleFile]);

  return [loading, upload];
};
